import type { BaseIssuesState, IssueFilters } from 'shared/issue_filters_context/types';
import {
    isValidCreatedByFilter,
    isValidFlaggedFilter,
    isValidHealthRangeFilter,
    isValidIssueHardwareType,
    isValidPriorityFilter,
    isValidSavingsFilter,
    isValidSeverityFilter,
    isValidStatusFilter,
    isValidTimeRangeFilter,
    isValidVisibilityFilter,
} from 'shared/issue_filters_context/validation';
import { createJSONStorage } from 'zustand/middleware';

/**
 * Creates a fresh copy of default issue filters.
 * Use this for reset operations to ensure React useEffect triggers properly.
 */
export function createDefaultIssueFilters(): IssueFilters {
    return {
        timeRangeFilter: undefined,
        statusFilters: [],
        severityFilters: [],
        priorityFilters: [],
        faultTypeFilters: [],
        issueLocationFilters: [],
        dynamicSystemFilters: [],
        savingsFilters: [],
        healthRangeFilter: undefined,
        hardwareTypeFilters: [],
        visibilityFilter: undefined,
        flaggedFilters: [],
        createdByFilter: undefined,
    };
}

/**
 * Default filter values for all issue contexts.
 * Single source of truth for initial filter state.
 */
export const DEFAULT_ISSUE_FILTERS: IssueFilters = createDefaultIssueFilters();

/**
 * Filter property keys derived from IssueFilters type.
 * This ensures we have a single source of truth for filter properties.
 */
export const FILTER_PROPERTY_KEYS = [
    'timeRangeFilter',
    'statusFilters',
    'severityFilters',
    'priorityFilters',
    'savingsFilters',
    'faultTypeFilters',
    'issueLocationFilters',
    'dynamicSystemFilters',
    'healthRangeFilter',
    'hardwareTypeFilters',
    'visibilityFilter',
    'flaggedFilters',
    'createdByFilter',
] as const;

/**
 * Helper function to create filter setters programmatically.
 * This generates all the individual filter setter methods to avoid repetitive code.
 *
 * @param set - The zustand set function for updating state
 * @returns Object containing all filter setter methods
 */
export function createFilterSetters(set: (partial: Partial<BaseIssuesState>) => void) {
    return {
        setStatusFilters: (statusFilters: BaseIssuesState['statusFilters']) =>
            set({ statusFilters }),
        setSeverityFilters: (severityFilters: BaseIssuesState['severityFilters']) =>
            set({ severityFilters }),
        setPriorityFilters: (priorityFilters: BaseIssuesState['priorityFilters']) =>
            set({ priorityFilters }),
        setSavingsFilters: (savingsFilters: BaseIssuesState['savingsFilters']) =>
            set({ savingsFilters }),
        setTimeRangeFilter: (timeRangeFilter: BaseIssuesState['timeRangeFilter']) =>
            set({ timeRangeFilter }),
        setHealthRangeFilter: (healthRangeFilter: BaseIssuesState['healthRangeFilter']) =>
            set({ healthRangeFilter }),
        setFaultTypeFilters: (faultTypeFilters: BaseIssuesState['faultTypeFilters']) =>
            set({ faultTypeFilters }),
        setIssueLocationFilters: (
            issueLocationFilters: BaseIssuesState['issueLocationFilters']
        ) => set({ issueLocationFilters }),
        setDynamicSystemFilters: (
            dynamicSystemFilters: BaseIssuesState['dynamicSystemFilters']
        ) => set({ dynamicSystemFilters }),
        setHardwareTypeFilters: (
            hardwareTypeFilters: BaseIssuesState['hardwareTypeFilters']
        ) => set({ hardwareTypeFilters }),
        setVisibilityFilter: (visibilityFilter: BaseIssuesState['visibilityFilter']) =>
            set({ visibilityFilter }),
        setFlaggedFilters: (flaggedFilters: BaseIssuesState['flaggedFilters']) =>
            set({ flaggedFilters }),
        setCreatedByFilter: (createdByFilter: BaseIssuesState['createdByFilter']) =>
            set({ createdByFilter }),
        setSort: (sort: BaseIssuesState['sort']) => set({ sort }),
    };
}

/**
 * Creates a batch update method for performance optimization.
 * This allows updating multiple filters at once to reduce re-renders.
 *
 * @param set - The zustand set function for updating state
 * @returns Batch update function
 */
export function createBatchFilterUpdate(
    set: (partial: Partial<BaseIssuesState>) => void
) {
    return (newFilters: IssueFilters) => {
        set(newFilters);
    };
}

/**
 * Generates a unique storage key for issue filter persistence.
 * Used by desk and other persistent filter contexts.
 *
 * @param context - The context (e.g., 'modal', 'desk')
 * @param entityType - The entity type (e.g., 'asset', 'assembly', 'location')
 * @param entityId - The entity identifier
 * @param filterType - The filter type (e.g., 'active', 'archived')
 * @returns Unique storage key string
 */
export function generateStorageKey(
    context: string,
    entityType: string,
    entityId: string,
    filterType: string
): string {
    return `${context}:${entityType}:${entityId}:${filterType}`;
}

/**
 * Creates standardized persistence configuration for issue filter stores.
 * Used by desk and other persistent filter contexts.
 *
 * @param storageKey - Unique storage key for this store instance
 * @param useSessionStorage - Whether to use sessionStorage (default) or localStorage
 * @returns Persistence configuration object
 */
export function createPersistenceConfig(
    storageKey: string,
    useSessionStorage: boolean = true
) {
    return {
        name: storageKey,
        storage: createJSONStorage(() =>
            useSessionStorage ? sessionStorage : localStorage
        ),
    };
}

/**
 * Pre-compiled selector for extracting filter properties efficiently.
 * This avoids creating new objects on every render in React components.
 */
export const filterPropertiesSelector = (state: BaseIssuesState) => {
    const result = {} as Record<string, unknown>;
    for (const key of FILTER_PROPERTY_KEYS) {
        result[key] = state[key];
    }
    return result;
};

/**
 * Mapping of filter state properties to their corresponding data source properties.
 * Used to determine which defaults to preserve when no explicit filters are set.
 */
export const FILTER_PRESERVATION_MAP: Record<keyof IssueFilters, string> = {
    statusFilters: 'statusType',
    priorityFilters: 'priority',
    severityFilters: 'severity',
    savingsFilters: 'savings',
    faultTypeFilters: 'faultTypeIds',
    issueLocationFilters: 'hierarchyGroupIds',
    dynamicSystemFilters: 'dynamicSystemIds',
    hardwareTypeFilters: 'hardwareTypes',
    timeRangeFilter: 'created',
    healthRangeFilter: 'hasHealthRanges',
    visibilityFilter: 'isInternal',
    flaggedFilters: 'isFlagged',
    createdByFilter: 'manuallyCreated',
};

/**
 * Checks if a filter state property is empty (no explicit user filters set).
 */
export function isFilterEmpty(filterValue: unknown): boolean {
    if (Array.isArray(filterValue)) {
        return filterValue.length === 0;
    }
    return filterValue === undefined;
}

/**
 * Shared validation constraints for issue filter state.
 * These constraints can be used by any context that needs to validate filter state.
 */
export const ISSUE_FILTER_VALIDATION_CONSTRAINTS = {
    priorityFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidPriorityFilter),
    severityFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidSeverityFilter),
    statusFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidStatusFilter),
    savingsFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidSavingsFilter),
    faultTypeFilters: (value: unknown) =>
        Array.isArray(value) && value.every((v) => typeof v === 'string'),
    issueLocationFilters: (value: unknown) =>
        Array.isArray(value) && value.every((v) => typeof v === 'string'),
    dynamicSystemFilters: (value: unknown) =>
        Array.isArray(value) && value.every((v) => typeof v === 'string'),
    hardwareTypeFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidIssueHardwareType),
    timeRangeFilter: isValidTimeRangeFilter,
    healthRangeFilter: isValidHealthRangeFilter,
    visibilityFilter: isValidVisibilityFilter,
    flaggedFilters: (value: unknown) =>
        Array.isArray(value) && value.every(isValidFlaggedFilter),
    createdByFilter: isValidCreatedByFilter,
} as const;
